import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment-timezone';

import { getStyles } from './styles';

const TIME_FORMAT = 'hh:mm a';

// Computes the display parts of a start/end range in a given timezone.
// `offset` (minutes from UTC at endTs) is used to de-dupe equivalent zones.
const buildWindow = (startTs, endTs, tz) => {
  const end = moment.tz(endTs, tz);
  return {
    range: `${moment.tz(startTs, tz).format(TIME_FORMAT)} - ${end.format(TIME_FORMAT)}`,
    zone: end.format('z'),
    offset: end.utcOffset(),
  };
};


// "range ZONE" with the timezone abbreviation rendered bold.
const renderRange = ({ range, zone }) => (
  <>
    {`${range} `}<Box component="span" sx={{ fontWeight: 'bold' }}>{zone}</Box>
  </>
);

function TimeWindow(props) {
  const {
    startTs,
    endTs,
    timezone,
    label,
    labelAdornment,
    variant = 'default', // 'default' | 'inline' | 'tooltip'
    renderTooltip, // optional (localWindow) => node for the tooltip variant, overrides the default tooltip content
    containerSx,
    labelSx,
    valueSx,
  } = props;

  const theme = useTheme();

  const isValidWindow = !!startTs && !!endTs && moment(startTs).isValid() && moment(endTs).isValid();

  const styles = getStyles(theme, { variant });

  const renderValue = () => {
    if (!isValidWindow) return <Box component="span" sx={[styles.window, valueSx]}>-</Box>;

    const localWindow = buildWindow(startTs, endTs, moment.tz.guess());
    const targetWindow = timezone ? buildWindow(startTs, endTs, timezone) : null;
    const hasDifferentOffset = !!targetWindow && targetWindow.offset !== localWindow.offset;
    const isTooltip = variant === 'tooltip';

    const getTooltipContent = () => {
      if (!isTooltip || !hasDifferentOffset) return '';
      if (renderTooltip) return renderTooltip(localWindow);
      return `[${localWindow.range} ${localWindow.zone}]`;
    };  

    return (
      <>
        <Tooltip title={getTooltipContent()} placement="top" arrow>
          <Box component="span" sx={[styles.window, valueSx]}>
            [{renderRange(targetWindow || localWindow)}]
          </Box>
        </Tooltip>
        {!isTooltip && hasDifferentOffset && (
          <Box component="span" sx={[styles.window, valueSx]}>
            [{renderRange(localWindow)}]
          </Box>
        )}
      </>
    );
  };

  return (
    <Box component="span" sx={[styles.container, containerSx]}>
      {label && (
        <Box component="span" sx={[styles.label, labelAdornment && styles.labelWithAdornment, labelSx]}>
          {label}
          {labelAdornment}
        </Box>
      )}
      <Box component="span" sx={styles.values}>{renderValue()}</Box>
    </Box>
  );
}

export default TimeWindow;
